import resolveResponse from "contentful-resolve-response";

import type { AccessToken, TokenCredential } from "@azure/core-auth";
import * as msalNode from "@azure/msal-node";

import { env } from "@/env";

import { CONTENT_TYPES, toErrorString, type Context } from "./globals";
import { messages } from "./globals.content";
import {
    BEITRAG_TYPES,
    FACH_KUERZEL,
    RFaecherScheme,
    RFiltersScheme,
    type RFiltersType,
    RProductScheme,
    type RProductType,
    RSearchProductScheme,
    RSearchProductsScheme,
    type RSearchProductsType,
    type RSearchProductType,
    SORT_TYPES,
} from "./schemes/raabits.scheme";

/**
 * See:
 * - https://learn.microsoft.com/en-us/javascript/api/overview/azure/identity-readme?view=azure-node-latest#when-to-use-something-else
 * - https://github.com/Azure/azure-sdk-for-js/blob/@azure/identity_4.5.0/sdk/identity/identity/samples/AzureIdentityExamples.md#authenticating-with-msal-directly
 * - https://github.com/AzureAD/microsoft-authentication-library-for-js/tree/dev/samples/msal-node-samples/client-credentials
 * - https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/master/lib/msal-node/docs/initialize-confidential-client-application.md#initializing-the-confidentialclientapplication-object
 * - https://learn.microsoft.com/en-us/entra/identity-platform/msal-logging-js
 */
class ConfidentialClientCredential implements TokenCredential {
    private cachedToken: AccessToken | null = null;

    // Number of milliseconds before the actual expiry when we should refresh the token
    private static readonly REFRESH_MARGIN_MS = 30 * 1000; // 30 seconds

    constructor(private confidentialApp: msalNode.ConfidentialClientApplication) {}

    public async getToken(scopes: string | string[]): Promise<AccessToken> {
        // Check if we have a cached token
        if (this.cachedToken) {
            // Calculate "time left" until token expiry
            const timeLeftMs = this.cachedToken.expiresOnTimestamp - Date.now();

            // If the token still has more than 30 seconds left, return it immediately.
            if (timeLeftMs > ConfidentialClientCredential.REFRESH_MARGIN_MS) {
                return this.cachedToken;
            }
        }

        // Otherwise, fetch a new token via client credentials flow
        const result = await this.confidentialApp.acquireTokenByClientCredential({
            scopes: Array.isArray(scopes) ? scopes : [scopes],
        });

        if (!result) {
            throw new Error("acquireTokenByClientCredential failed. No token was returned.");
        }

        const newToken: AccessToken = {
            token: result.accessToken,
            expiresOnTimestamp: result.expiresOn!.getTime(),
        };

        this.cachedToken = newToken;
        return newToken;
    }
}

/**
 * We store a singleton instance of the ConfidentialClientCredential here.
 */
let singletonCredential: ConfidentialClientCredential | null = null;

/**
 * Returns the singleton instance of ConfidentialClientCredential.
 * If it doesn't exist yet, we create it once.
 */
async function getConfidentialClientCredential(): Promise<ConfidentialClientCredential> {
    if (!singletonCredential) {
        const confidentialClient = new msalNode.ConfidentialClientApplication({
            auth: {
                clientId: env.RAABITS_AZURE_CLIENT_ID,
                authority: `https://login.microsoftonline.com/${env.RAABITS_AZURE_TENANT_ID}`,
                clientSecret: env.RAABITS_AZURE_CLIENT_SECRET,
            },
            system: {
                loggerOptions: {
                    logLevel: msalNode.LogLevel.Info,
                    loggerCallback: (level, message, containsPii) => {
                        if (containsPii) {
                            return;
                        }
                        switch (level) {
                            case msalNode.LogLevel.Error:
                                console.error("[MSAL]", message);
                                return;
                            case msalNode.LogLevel.Warning:
                                //console.warn("[MSAL]", message);
                                return;
                            case msalNode.LogLevel.Info:
                                //console.info("[MSAL]", message);
                                return;
                            case msalNode.LogLevel.Verbose:
                                //console.debug("[MSAL]", message);
                                return;
                        }
                    },
                    piiLoggingEnabled: false,
                },
            },
        });

        singletonCredential = new ConfidentialClientCredential(confidentialClient);
    }

    return singletonCredential;
}

const getAuthHeader = async () => {
    const credential = await getConfidentialClientCredential();
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
    const accessToken = await credential.getToken(env.RAABITS_AZURE_SCOPE);
    return `Bearer ${accessToken.token}`;
};

export const getRFaecher = async (): Promise<Record<string, string>> => {
    const res = await fetch(
        `https://cdn.contentful.com/spaces/${env.RAABITS_CF_SPACE_ID}/environments/${env.RAABITS_CF_ENVIRONMENT}/entries?` +
            Object.entries({
                access_token: env.RAABITS_CF_CDA_TOKEN,
                content_type: CONTENT_TYPES.faecher,
            })
                .map((p) => p.join("="))
                .join("&"),
        { next: { revalidate: 3600 } },
    );
    if (res.ok) {
        try {
            return RFaecherScheme.parse(resolveResponse(await res.json())).reduce<
                Record<string, string>
            >((a, b) => {
                a[b.fields.id] = b.fields.displayValue;
                return a;
            }, {});
        } catch (error) {
            console.error(error);
            return {};
        }
    } else {
        const logURL =
            `https://cdn.contentful.com/spaces/${env.RAABITS_CF_SPACE_ID}/environments/${env.RAABITS_CF_ENVIRONMENT}/entries?` +
            Object.entries({
                content_type: CONTENT_TYPES.faecher,
            })
                .map((p) => p.join("="))
                .join("&");

        console.error("FAILED getRFaecher: " + logURL, res.status, res.statusText);
        return {};
    }
};

export const getRFilters = async () => {
    const headers = { Authorization: await getAuthHeader() };
    const res = await fetch(env.RAABITS_BACKEND_URI + "possible-filter-values", {
        next: { revalidate: 3600 },
        headers,
    });
    if (res.ok) {
        const faecher = await getRFaecher();
        try {
            const filters = RFiltersScheme.parse(await res.json());

            return {
                schularten: filters.schularten.reduce<Record<string, string>>((a, b) => {
                    a[b] = b;
                    return a;
                }, {}),
                klassenstufen: filters.klassenstufen.reduce<Record<string, string>>((a, b) => {
                    if (!isNaN(parseInt(b))) {
                        a[b] = `${b}${messages.unterrichtsmaterial.klassenstufeSuffix}`;
                    } else {
                        a[b] = b;
                    }

                    return a;
                }, {}),
                faecher: filters.faecher.reduce<Record<string, string>>((a, b) => {
                    a[b] = faecher[b] ?? b;
                    return a;
                }, {}),
                lernjahre: filters.klassenstufen.reduce<Record<string, string>>((a, b) => {
                    a[b] = `${b}. ${messages.meta.lernjahr}`;
                    return a;
                }, {}),
            };
        } catch (error) {
            console.error(error);
            return undefined;
        }
    } else {
        console.error(
            "FAILED getRFilters: " + env.RAABITS_BACKEND_URI + "possible-filter-values",
            res.status,
            res.statusText,
        );
        return undefined;
    }
};

export const getRProduct = async (
    rnumber: string | undefined,
): Promise<[RProductType, string] | [string, string]> => {
    try {
        if (rnumber) {
            const res = await fetch(env.RAABITS_BACKEND_URI + rnumber + "/unsubscribed", {
                headers: { Authorization: await getAuthHeader() },
                next: { revalidate: 3600 },
            });

            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            const json = await res.json();

            if (res.ok) {
                const product = RProductScheme.safeParse(json);

                if (!product.success) {
                    return [toErrorString(product.error), JSON.stringify(json, null, 2)];
                } else {
                    return [product.data, JSON.stringify(json, null, 2)];
                }
            } else {
                return [res.status.toString(), JSON.stringify(json, null, 2)];
            }
        } else {
            return ["Missing RNumber", ""];
        }
    } catch (error) {
        return [toErrorString(error), ""];
    }
};

export const getRSearchProduct = async (
    rnumber: string | undefined,
): Promise<RSearchProductType | undefined> => {
    if (rnumber) {
        const res = await fetch(process.env.RAABITS_BACKEND_URI + "search/" + rnumber, {
            headers: { Authorization: await getAuthHeader() },
            next: { revalidate: 3600 },
        });
        if (res.ok) {
            const f = RSearchProductScheme.safeParse(await res.json());
            return f.data;
        }
    }
    return undefined;
};

export async function search({
    searchTerm,
    page,
    entriesPerPage,
    ...filters
}: {
    entriesPerPage: number;
    page: number;
    searchTerm: string;
} & RFiltersType): Promise<RSearchProductsType> {
    const url = new URL(process.env.RAABITS_BACKEND_URI + "search");
    url.searchParams.append("searchTerm", searchTerm ?? "");
    url.searchParams.append("page", page.toString());
    url.searchParams.append("entriesPerPage", entriesPerPage.toString());
    url.searchParams.append("sortType", SORT_TYPES.FACH_OVERVIEW);

    if (filters.schularten.length) {
        url.searchParams.append("schularten", filters.schularten.join(","));
    }
    if (filters.klassenstufen.length) {
        url.searchParams.append("klassenstufen", filters.klassenstufen.join(","));
    }
    if (filters.faecher.length) {
        url.searchParams.append("faecher", filters.faecher.join(","));
    }
    const res = await fetch(url.toString(), {
        headers: { Authorization: await getAuthHeader() },
        next: { revalidate: 3600 },
    });

    if (res.ok) {
        try {
            return RSearchProductsScheme.parse(await res.json());
        } catch (error) {
            console.error(error);
            return { produkte: [], totalResults: 0 };
        }
    } else {
        console.error("FAILED: " + url.toString(), res.status, res.statusText);
        return { produkte: [], totalResults: 0 };
    }
}

export function getSearchAbo(product: RSearchProductType, context: Context): string {
    return product.beitragsType && product.isInRaabits
        ? product.filterMetadata.fach === FACH_KUERZEL.GLE.toString()
            ? context.content?.["product-card"]["buy-abo-premium-plus"] || "what?"
            : product.filterMetadata.fach === FACH_KUERZEL.GGR.toString()
              ? context.content?.["product-card"]["buy-abo-premium"] || "what?"
              : [BEITRAG_TYPES.ST1, BEITRAG_TYPES.ST2].includes(product.beitragsType)
                ? context.content?.["product-card"]["buy-abo-basis"] || "what?"
                : [BEITRAG_TYPES.MM1, BEITRAG_TYPES.IN1].includes(product.beitragsType)
                  ? context.content?.["product-card"]["buy-abo-premium"] || "what?"
                  : context.content?.["product-card"]["buy-abo-premium-plus"] || "what?"
        : context.content?.["product-card"]["buy-product"] || "what?";
}

export function getProductAbo(product: RProductType, context: Context): string {
    return product.metadata.beitragsType && product.metadata.isInRaabits
        ? product.metadata.fach === FACH_KUERZEL.GLE.toString()
            ? context.content?.["product-card"]["buy-abo-premium-plus"] || "what?"
            : product.metadata.fach === FACH_KUERZEL.GGR.toString()
              ? context.content?.["product-card"]["buy-abo-premium"] || "what?"
              : [BEITRAG_TYPES.ST1, BEITRAG_TYPES.ST2].includes(product.metadata.beitragsType)
                ? context.content?.["product-card"]["buy-abo-basis"] || "what?"
                : [BEITRAG_TYPES.MM1, BEITRAG_TYPES.IN1].includes(product.metadata.beitragsType)
                  ? context.content?.["product-card"]["buy-abo-premium"] || "what?"
                  : context.content?.["product-card"]["buy-abo-premium-plus"] || "what?"
        : context.content?.["product-card"]["buy-product"] || "what?";
}
